import React, { useEffect, useState } from 'react';
import RootRouter from './portals/RootRouter';
import { LanguageProvider } from './components/LanguageContext';
import './App.css';
import { LegalModalHost } from './portals/shared/LegalModal';

const API_URL = process.env.REACT_APP_API_URL || 'https://investmenter-backend-1081814658115.europe-west1.run.app';

// Shown when the team pauses the app (maintenance mode) to roll out an update.
// Controlled server-side via config/appStatus — no redeploy needed to toggle.
const MaintenanceScreen: React.FC<{ message?: string }> = ({ message }) => (
  <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: 28, background: 'linear-gradient(180deg,#0f1e33,#0b1526)', color: '#fff' }}>
    <div style={{ width: 68, height: 68, borderRadius: 20, background: 'rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 34, marginBottom: 20 }}>🛠️</div>
    <h1 style={{ fontSize: 24, fontWeight: 800, margin: 0 }}>We're upgrading</h1>
    <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.75)', maxWidth: 360, marginTop: 12, lineHeight: 1.6 }}>
      {message || 'The app is briefly unavailable while we roll out improvements. Please check back shortly — thank you for your patience.'}
    </p>
  </div>
);

function App() {
  const [maintenance, setMaintenance] = useState<{ on: boolean; message?: string }>({ on: false });

  useEffect(() => {
    const checkStatus = async () => {
      try {
        const lastRefresh = localStorage.getItem('lastRefreshTimestamp');
        const response = await fetch(`${API_URL}/api/app-version`);
        const data = await response.json();

        // Maintenance / pause gate
        if (data.maintenance) {
          setMaintenance({ on: true, message: data.maintenanceMessage });
          return;
        }

        if (data.forceRefresh && data.forceRefreshTimestamp) {
          if (!lastRefresh || parseInt(lastRefresh) < data.forceRefreshTimestamp) {
            const shouldUpdate = window.confirm(
              '🔄 A new version is available!\n\nTap OK to update the app.'
            );

            if (shouldUpdate) {
              localStorage.setItem('lastRefreshTimestamp', data.forceRefreshTimestamp.toString());
              if ('caches' in window) {
                const cacheNames = await caches.keys();
                await Promise.all(cacheNames.map(name => caches.delete(name)));
              }
              window.location.reload();
            }
          }
        }
      } catch (error) {
      }
    };

    checkStatus();
  }, []);

  if (maintenance.on) return <MaintenanceScreen message={maintenance.message} />;

  return (
    <LanguageProvider>
      <RootRouter />
      <LegalModalHost />
    </LanguageProvider>
  );
}

export default App;
